import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import { formatDisplayShort } from '../../../lib/dateUtils';

/**
 * Organisations list — desktop rows + mobile cards (mirrors TeamMembersList).
 * Presentation only; status mutations stay in the parent.
 */
export default function OrganizationsList({
  organizations,
  currentOrganizationId,
  onSetStatus,
  isUpdating,
}) {
  const rows = organizations || [];

  return (
    <div className="card team-card">
      <div className="team-table-desktop">
        <table className="table team-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Status</th>
              <th>Users</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((org) => (
              <OrgDesktopRow
                key={org.id}
                org={org}
                isOwn={org.id === currentOrganizationId}
                onSetStatus={onSetStatus}
                isUpdating={isUpdating}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="team-mobile-cards">
        {rows.map((org) => (
          <OrgMobileCard
            key={org.id}
            org={org}
            isOwn={org.id === currentOrganizationId}
            onSetStatus={onSetStatus}
            isUpdating={isUpdating}
          />
        ))}
      </div>

      {rows.length === 0 && (
        <p className="empty-state">No organisations match these filters.</p>
      )}
    </div>
  );
}

function StatusBadge({ active }) {
  return (
    <Badge variant={active ? 'success' : 'danger'}>
      {active ? 'Active' : 'Suspended'}
    </Badge>
  );
}

function StatusAction({ org, isOwn, onSetStatus, isUpdating }) {
  if (org.active) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onSetStatus(org, false)}
        disabled={isUpdating || isOwn}
        title={isOwn ? 'You cannot suspend your own organisation' : 'Suspend organisation'}
        aria-label={isOwn ? 'Cannot suspend own organisation' : `Suspend ${org.name}`}
      >
        Suspend
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={() => onSetStatus(org, true)}
      disabled={isUpdating}
      title="Reactivate organisation"
      aria-label={`Reactivate ${org.name}`}
    >
      Reactivate
    </Button>
  );
}

function OrgDesktopRow({ org, isOwn, onSetStatus, isUpdating }) {
  return (
    <tr>
      <td>{org.name || '—'}</td>
      <td>{org.code || '—'}</td>
      <td><StatusBadge active={!!org.active} /></td>
      <td>{org.user_count ?? 0}</td>
      <td>{org.created_at ? formatDisplayShort(org.created_at) : '—'}</td>
      <td>
        <div className="team-table-actions">
          <StatusAction
            org={org}
            isOwn={isOwn}
            onSetStatus={onSetStatus}
            isUpdating={isUpdating}
          />
        </div>
      </td>
    </tr>
  );
}

function OrgMobileCard({ org, isOwn, onSetStatus, isUpdating }) {
  return (
    <div className="team-mobile-card">
      <div className="team-mobile-card-name">{org.name || '—'}</div>
      <div className="team-mobile-card-email">{org.code || '—'}</div>
      <div className="team-mobile-card-role">
        <StatusBadge active={!!org.active} />
      </div>
      <div className="team-mobile-card-status text-muted" style={{ fontSize: '0.875rem' }}>
        {org.user_count ?? 0} users
        {org.created_at ? ` · Created ${formatDisplayShort(org.created_at)}` : ''}
      </div>
      <div className="team-mobile-card-actions">
        <StatusAction
          org={org}
          isOwn={isOwn}
          onSetStatus={onSetStatus}
          isUpdating={isUpdating}
        />
      </div>
    </div>
  );
}
